
import { mountThemeFeature } from './editor-lifecycle.js';

mountThemeFeature(lifecycle => {
const { IntersectionObserver, ResizeObserver, setTimeout, setInterval, requestAnimationFrame } = lifecycle;
/* ============================================================
   SKIN BEAUTIFUL MD — BLEND engine
   Light Phone's mechanism, unchanged:  1 backdrop dissolve (IO),
   2 frosted nav on scroll, 3 reveals, 4 tab→backdrop path,
   5 rotating quotes.  Added from SURI: 6 the drawer (blur scrim,
   12px nudge, real tablist), 8 the unblur entrance.
   No animation library. Still ~200 lines, on purpose.
   ============================================================ */

const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;
const html = document.documentElement;

/* ---------- 1. backdrop dissolve (scroll path) ---------- */
const layers = new Map();
document.querySelectorAll('.backdrop[data-layer]').forEach(el => layers.set(el.dataset.layer, el));
let tabOverride = null;
const sectionState = new Map();
function showLayer(id) { layers.forEach((el, key) => el.classList.toggle('is-visible', key === id)); }
const bdObserver = new IntersectionObserver(entries => {
  entries.forEach(e => sectionState.set(e.target, e.isIntersecting));
  const active = [...sectionState.entries()].filter(([, v]) => v).map(([s]) => s)
    .sort((a, b) => b.getBoundingClientRect().top - a.getBoundingClientRect().top)[0];
  if (!active) { layers.forEach(el => el.classList.remove('is-visible')); return; }
  let id = active.dataset.backdrop;
  if (tabOverride && tabOverride.section === active) id = tabOverride.layerId;
  if (id) showLayer(id); else layers.forEach(el => el.classList.remove('is-visible'));
}, { rootMargin: '0px 0px -50% 0px' });
document.querySelectorAll('section[data-backdrop]').forEach(s => bdObserver.observe(s));

/* ---------- 2. nav frost ---------- */
const header = document.querySelector('.site-header');
if (header) {
  let frame = 0;
  const check = () => { frame = 0; header.classList.toggle('is-scrolled', scrollY > 8); };
  lifecycle.listen(window, 'scroll', () => { if (!frame) frame = requestAnimationFrame(check); }, { passive: true });
  check();
}

/* ---------- 3. reveals + 8. unblur ---------- */
const revealObserver = new IntersectionObserver(entries => {
  entries.forEach(e => { if (e.isIntersecting) { e.target.classList.add('is-in'); revealObserver.unobserve(e.target); } });
}, { rootMargin: '0px 0px -12% 0px' });
document.querySelectorAll('.reveal, .unblur').forEach(el => revealObserver.observe(el));
document.querySelectorAll('.reveal-group').forEach(group => {
  [...group.children].forEach((child, i) => {
    child.classList.add('reveal');
    if (!reduced) child.style.transitionDelay = `${i * 90}ms`;
    revealObserver.observe(child);
  });
});

/* ---------- 4. tabs (routine + drawer). Tabs with data-backdrop dissolve too ---------- */
document.querySelectorAll('[role="tablist"]').forEach(list => {
  const tabs = [...list.querySelectorAll('[role="tab"]')];
  const panels = tabs.map(t => document.getElementById(t.getAttribute('aria-controls'))).filter(Boolean);
  function select(tab) {
    tabs.forEach(t => { const on = t === tab; t.setAttribute('aria-selected', on); t.tabIndex = on ? 0 : -1; });
    panels.forEach(p => { const on = p.id === tab.getAttribute('aria-controls'); p.toggleAttribute('hidden', !on); });
    /* SURI-contained dissolve: a tab with data-layer-target cross-dissolves
       the [data-layer] tiles inside its own section (Light Phone timing) */
    const target = tab.dataset.layerTarget;
    if (target) {
      const scope = tab.closest('section') || document;
      scope.querySelectorAll('[data-layer]').forEach(el => el.classList.toggle('is-visible', el.dataset.layer === target));
    }
    /* legacy fixed-backdrop path (kept for pages that use it) */
    const bd = tab.dataset.backdrop;
    if (bd) {
      const section = tab.closest('section[data-backdrop]');
      tabOverride = section ? { section, layerId: bd } : null;
      showLayer(bd);
      if (section) section.classList.toggle('routine--ink', tab.hasAttribute('data-ink'));
    }
  }
  tabs.forEach((tab, i) => {
    lifecycle.listen(tab, 'click', () => select(tab));
    lifecycle.listen(tab, 'keydown', e => {
      let j = null;
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') j = (i + 1) % tabs.length;
      if (e.key === 'ArrowLeft'  || e.key === 'ArrowUp')   j = (i - 1 + tabs.length) % tabs.length;
      if (e.key === 'Home') j = 0; if (e.key === 'End') j = tabs.length - 1;
      if (j !== null) { e.preventDefault(); tabs[j].focus(); select(tabs[j]); }
    });
  });
  list.__select = select;
  list.__tabs = tabs;
});

/* ---------- 6. drawer (SURI) ---------- */
const drawer = document.querySelector('.drawer');
const scrim  = document.querySelector('.drawer-scrim');
if (drawer && scrim) {
  const openers = document.querySelectorAll('[data-drawer]');
  const closeBtn = drawer.querySelector('.drawer-close');
  const list = drawer.querySelector('[role="tablist"]');
  let lastFocus = null;
  const open = tabId => {
    lastFocus = document.activeElement;
    if (tabId && list?.__select) {
      const t = list.__tabs.find(t => t.dataset.tab === tabId);
      if (t) list.__select(t);
    }
    html.classList.add('drawer-open');
    openers.forEach(b => b.setAttribute('aria-expanded', 'true'));
    drawer.removeAttribute('aria-hidden');
    setTimeout(() => (list?.querySelector('[aria-selected="true"]') || closeBtn).focus(), reduced ? 0 : 200);
  };
  const close = () => {
    html.classList.remove('drawer-open');
    openers.forEach(b => b.setAttribute('aria-expanded', 'false'));
    drawer.setAttribute('aria-hidden', 'true');
    lastFocus?.focus();
  };
  openers.forEach(b => lifecycle.listen(b, 'click', e => { e.preventDefault(); open(b.dataset.drawer); }));
  lifecycle.listen(closeBtn, 'click', close);
  lifecycle.listen(scrim, 'click', close);
  lifecycle.listen(drawer, 'click', e => { if (e.target.closest('a[href^="#"]')) close(); });
  lifecycle.listen(window, 'keydown', e => { if (e.key === 'Escape' && html.classList.contains('drawer-open')) close(); });
  /* focus trap — keep tab inside the panel while open */
  lifecycle.listen(drawer, 'keydown', e => {
    if (e.key !== 'Tab') return;
    const f = [...drawer.querySelectorAll('a[href],button:not([disabled]),[tabindex="0"]')].filter(el => el.offsetParent !== null);
    if (!f.length) return;
    const first = f[0], last = f[f.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  });
  drawer.setAttribute('aria-hidden', 'true');
}

/* ---------- 5. rotating testimonials ---------- */
document.querySelectorAll('[data-quotes]').forEach(stage => {
  const quotes = [...stage.querySelectorAll('.quote')];
  if (!quotes.length) return;
  let i = 0; quotes[0].classList.add('is-active');
  if (quotes.length < 2) return;
  const show = dir => {
    const prev = quotes[i]; prev.classList.remove('is-active'); prev.classList.add('is-prev');
    setTimeout(() => prev.classList.remove('is-prev'), 700);
    i = (i + dir + quotes.length) % quotes.length; quotes[i].classList.add('is-active');
  };
  let timer = 0; const arm = () => { if (!reduced) timer = setInterval(() => show(1), 9000); }; arm();
  let x0 = null;
  lifecycle.listen(stage, 'touchstart', e => { x0 = e.touches[0].clientX; }, { passive: true });
  lifecycle.listen(stage, 'touchend', e => {
    if (x0 === null) return; const dx = e.changedTouches[0].clientX - x0; x0 = null;
    if (Math.abs(dx) < 40) return; clearInterval(timer); show(dx < 0 ? 1 : -1); arm();
  }, { passive: true });
});

/* ---------- image decode fade ---------- */
document.querySelectorAll('img.img-fade').forEach(img => {
  if (img.complete) img.classList.add('is-loaded');
  else lifecycle.listen(img, 'load', () => img.classList.add('is-loaded'), { once: true });
});

/* ---------- footer signup: native post to Shopify's customer form.
   The return path carries customer_posted=true so the capture popup's
   script answers success with its Welcome state (and remembers the
   subscription). Search params ride along — the myshopify preview's
   preview_theme_id must survive the redirect. ---------- */
document.querySelectorAll('[data-signup-return]').forEach(inp => {
  const q = location.search ? location.search + '&customer_posted=true' : '?customer_posted=true';
  inp.value = location.pathname + q;
});

});
